import React, { useEffect, useRef, useState } from 'react';
import './Map.css'; 

// Ообъявляем компонент карты 
const LocationMap = ({ locations = [], className = "" }) => {
  // Создаем ссылку на DOM-элемент карты
  const mapRef = useRef(null);
  // реф для хранения самой карты
  const mapInstance = useRef(null);
  // элементы управления состоянием
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Инициализация карты
  useEffect(() => {
    const initializeMap = async () => {
      try {
        setLoading(true);
        setError(null);
        
        // Промис, который ожидает, пока Yandex Maps станет готов к работе
        await new Promise((resolve, reject) => {
          // window.ymaps - глабальный объект, который появляется после загрузки скрипта Yandex Maps API
          if (window.ymaps) {
            // Если объект существует, ожидаем готовности карты
            window.ymaps.ready(resolve);
            return;
          }

          const script = document.createElement('script');
          script.src = `https://api-maps.yandex.ru/2.1/?apikey=REACT_APP_YMAPS_KEY&lang=ru_RU&load=package.full`;
          // скрипт загружается асинхронно, не блокируя рендер страницы
          script.async = true;
          // когда скрипт успешно загружен
          script.onload = () => window.ymaps.ready(resolve);
          script.onerror = reject;
          // скрипт добавляется в <head> документа
          document.head.appendChild(script);
        });
        
        // сохраняем ссылку на глобальный объект Yandex Maps API
        const ymaps = window.ymaps;

        // Центр и zoom
        let center, zoom;
        // Если одна метка - центрируем и зумим по ней
        if (locations.length === 1) {
          center = locations[0].coords;
          zoom = 16;
        } else { // Если несколько меток - центрируем по средним координатам
          const avgLat = locations.reduce((sum, loc) => sum + loc.coords[0], 0) / locations.length;
          const avgLon = locations.reduce((sum, loc) => sum + loc.coords[1], 0) / locations.length;
          center = [avgLat, avgLon];
          zoom = 13;
        }

        // Создаем экземпляр карты
        // mapRef.current - это сам <div>, который существует в документе после рендера
        mapInstance.current = new ymaps.Map(mapRef.current, {
          center: center,
          zoom: zoom,
          // 'zoomControl' - Кнопки «+» и «–» для масштабирования, 'typeSelector' - Выпадающий список типа карты
          controls: ['zoomControl', 'typeSelector']
        });

        // Переменная для отслеживания текущего открытого балуна
        let currentOpenBalloon = null;

        // Создаем массив меток - placemarks
        // Проходим по всем локациям и создаем для каждой объект placemark
        const placemarks = locations.map((location) => {
          // Конструктор метки. Здесь используются только первые два параметра:
          // coords — положение метки, properties — данные, которые отображаются (заголовок, описание и т.д.).
          const placemark = new ymaps.Placemark(
            location.coords,
            {
              balloonContentHeader: `<strong>${location.title}</strong>`, // Заголовок балуна
              // Основное содержимое балуна
              balloonContentBody: `
                <div style="max-width: 250px;">
                  ${location.address ? `<p style="margin: 5px 0; font-size: 12px; color: #666;">📍 ${location.address}</p>` : ''}
                </div>
              `,
              hintContent: location.title // Подсказка при наведении - только название
            },
            {
              preset: locations.length === 1 ? 'islands#redIcon' : 'islands#blueIcon',
              iconColor: locations.length === 1 ? '#ff0000' : '#1E88E5',
              // Включаем балун и подсказку
              balloonCloseButton: true,
              hideIconOnBalloonOpen: false
            }
          );

          // Обработчик клика по метке
          placemark.events.add('click', (e) => {
            // e.get('target') — получает объект метки, по которой кликнули.
            const target = e.get('target');
            
            // Закрываем предыдущий балун если он открыт
            if (currentOpenBalloon && currentOpenBalloon.balloon && currentOpenBalloon.balloon.isOpen()) {
              currentOpenBalloon.balloon.close();
            }
            
            // Открываем балун текущей метки
            target.balloon.open();
            currentOpenBalloon = target;
          });

          // Обработчик закрытия балуна
          placemark.events.add('balloonclose', () => {
            if (currentOpenBalloon === placemark) {
              currentOpenBalloon = null;
            }
          });

          return placemark;
        });

        // Добавляем метки на карту
        placemarks.forEach(placemark => {
          mapInstance.current.geoObjects.add(placemark);
        });

        // Для одной метки - открываем балун автоматически при загрузке
        if (locations.length === 1 && placemarks[0]) {
          setTimeout(() => {
            placemarks[0].balloon.open();
            currentOpenBalloon = placemarks[0];
            // Карта и API могут быть ещё не полностью готовы к открытию балуна сразу после создания.
          // Задержка в 800 мс даёт время на окончательную инициализацию карты.
          }, 800);
        }

        // Для нескольких меток - подстраиваем карту
        if (locations.length > 1) {
          // getBounds() — вычисляет ограничивающий прямоугольник (bounding box) вокруг всех добавленных геообъектов.
          const bounds = mapInstance.current.geoObjects.getBounds();
          if (bounds) {
            mapInstance.current.setBounds(bounds, {
              // безопасная установка зума
              checkZoomRange: true,
              // отступ от краёв меток (в пикселях)
              zoomMargin: 50
            });
          }
        }

        // Обработчик клика по карте - закрывает все балуны
        mapInstance.current.events.add('click', () => {
          if (currentOpenBalloon && currentOpenBalloon.balloon && currentOpenBalloon.balloon.isOpen()) {
            currentOpenBalloon.balloon.close();
            currentOpenBalloon = null;
          }
        });

        setLoading(false);

      } catch (err) {
        console.error('Map error:', err);
        setError(err.message);
        setLoading(false);
      }
    };

    initializeMap();

    // Функция очистки (cleanup) в useEffect
    return () => {
      if (mapInstance.current) {
        mapInstance.current.destroy();
      }
    };
  }, [locations]);

  return (
    <div className={`mapWrapper flex-column ${className}`} style={{ position: 'relative' }}>
      {loading && (
        <div className="map-loading">
          Загрузка карты...
        </div>
      )}

      {error && (
        <div className="map-error">
          <p>Ошибка: {error}</p>
        </div>
      )}
      
      {/* Отображение карты */}
      <div 
        ref={mapRef} 
        className="map"
      />
    </div>
  );
};

export default LocationMap;